import { Check, ChevronDown } from "lucide-react"
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react"
import { createPortal } from "react-dom"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { buildAudioInputOptions, type AudioInputDevice } from "@/lib/audio-inputs"
import { cn } from "@/lib/utils"

/** Имена микрофонов длинные: узкое меню обрезало бы их до бессмысленного «Default — Mac…». */
const MENU_MIN_WIDTH_PX = 15 * 16

type Props = {
  devices: AudioInputDevice[]
  /** "" — вход по умолчанию, иначе deviceId (см. useAudioInputPicker). */
  value: string
  onSelect: (value: string) => void
  pendingLabel: string | null
  defaultDeviceId: string | null
  holdToRecord: boolean
  onHoldToRecordChange: (next: boolean) => void
  /** Во время записи и распознавания: смена входа на ходу писала бы уже не с того микрофона. */
  disabled?: boolean
  className?: string
}

/** Выбор микрофона и режима кнопки надиктовки: шеврон рядом с кнопкой записи. */
export function VoiceInputMenu({
  devices,
  value,
  onSelect,
  pendingLabel,
  defaultDeviceId,
  holdToRecord,
  onHoldToRecordChange,
  disabled = false,
  className,
}: Props) {
  const { t } = useTranslation("common")
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const focusedOnOpenRef = useRef(false)

  const options = buildAudioInputOptions({
    devices,
    value,
    pendingLabel,
    defaultDeviceId,
    labels: {
      defaultInput: t("voiceDictation.defaultMicrophone"),
      defaultInputNamed: (name) => t("voiceDictation.defaultInputNamed", { name }),
      microphoneN: (n) => t("voiceDictation.microphoneN", { n }),
    },
  })

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    disabled,
    // Кнопка живёт у нижнего края композера; если сверху не хватит места, хук развернёт меню сам.
    direction: "up",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    positionKey: options.length,
    estimatedMenuWidthPx: MENU_MIN_WIDTH_PX,
    resolveWidth: () => ({
      minWidth: MENU_MIN_WIDTH_PX,
      width: "max-content",
      maxWidth: "min(22rem, calc(100vw - 16px))",
    }),
    onOutsideClick: () => setOpen(false),
  })

  // Запись началась с открытым меню — закрываем: иначе оно останется висеть
  // без позиционирования (disabled гасит menuStyle), а клик по шеврону лишь «переоткроет».
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  useEffect(() => {
    if (!open) return
    const itemsInMenu = () => {
      const menu = menuRef.current
      if (!menu) return [] as HTMLElement[]
      return [...menu.querySelectorAll<HTMLElement>('[role="menuitemradio"], [role="menuitemcheckbox"]')]
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        setOpen(false)
        buttonRef.current?.focus()
        return
      }
      const items = itemsInMenu()
      if (items.length === 0) return
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault()
        const delta = e.key === "ArrowDown" ? 1 : -1
        const current = items.indexOf(document.activeElement as HTMLElement)
        const start = current < 0 ? (delta > 0 ? -1 : 0) : current
        items[(start + delta + items.length) % items.length]?.focus()
        return
      }
      if (e.key === "Home") {
        e.preventDefault()
        items[0]?.focus()
        return
      }
      if (e.key === "End") {
        e.preventDefault()
        items[items.length - 1]?.focus()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open])

  useEffect(() => {
    if (!open) {
      focusedOnOpenRef.current = false
      return
    }
    if (focusedOnOpenRef.current || !menuStyle) return
    const menu = menuRef.current
    if (!menu) return
    const radios = [...menu.querySelectorAll<HTMLElement>('[role="menuitemradio"]')]
    const selected = radios.find((el) => el.getAttribute("aria-checked") === "true") ?? radios[0]
    selected?.focus()
    focusedOnOpenRef.current = true
  }, [open, menuStyle])

  const menu =
    open && !disabled && menuStyle && portalRoot ? (
      <div
        ref={menuRef}
        role="menu"
        aria-label={t("voiceDictation.microphoneSettings")}
        data-panel-wheel-isolate
        style={menuStyle}
        className="max-h-80 overflow-auto rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40"
      >
        <div role="presentation" className="px-3 pt-1 pb-1.5 text-[11px] font-medium text-ui-text-muted">
          {t("voiceDictation.microphone")}
        </div>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="menuitemradio"
            aria-checked={option.value === value}
            onClick={() => {
              onSelect(option.value)
              setOpen(false)
            }}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-ui-accent/25",
              option.value === value ? "text-ui-accent-text" : "text-ui-text-primary",
            )}
          >
            <span className="min-w-0 flex-1 truncate">{option.label}</span>
            {option.value === value ? (
              <Check className="h-3.5 w-3.5 shrink-0 text-ui-accent" />
            ) : (
              <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
          </button>
        ))}
        <div className="my-1 border-t border-ui-border" role="separator" />
        <button
          type="button"
          role="menuitemcheckbox"
          aria-checked={holdToRecord}
          // Меню не закрываем: переключатель тут же показывает новое состояние.
          onClick={() => onHoldToRecordChange(!holdToRecord)}
          className="flex w-full items-center gap-3 px-3 py-1.5 text-left text-xs text-ui-text-primary transition-colors hover:bg-ui-accent/25"
        >
          <span className="min-w-0 flex-1">{t("voiceDictation.holdToRecord")}</span>
          <span
            aria-hidden
            className={cn(
              "relative h-4 w-7 shrink-0 rounded-full transition-colors",
              holdToRecord ? "bg-ui-accent" : "dark:bg-ui-surface-raised light:bg-clay-4",
            )}
          >
            <span
              className={cn(
                "absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform",
                holdToRecord && "translate-x-3",
              )}
            />
          </span>
        </button>
      </div>
    ) : null

  const hint = t("voiceDictation.microphoneSettings")
  const trigger = (
    <Button
      type="button"
      ref={buttonRef}
      variant="ghost"
      size="sm"
      disabled={disabled}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={hint}
      className="h-8 w-5 rounded-lg p-0 hover:bg-ui-surface-hover"
      onClick={() => setOpen((prev) => !prev)}
      onKeyDown={(e: ReactKeyboardEvent<HTMLButtonElement>) => {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return
        e.preventDefault()
        setOpen(true)
      }}
    >
      <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
    </Button>
  )

  return (
    <div ref={rootRef} className={cn("relative inline-flex shrink-0 self-stretch", className)}>
      {trigger}
      {menu && portalRoot ? createPortal(menu, portalRoot) : null}
    </div>
  )
}
